import { Dayjs } from "dayjs";
import {
    classifyAvailsLicenseWindowStatus,
    pacificCalendarDateFromAvailsValue,
} from "../../../utilities/helpers/formatUnix";
import { AvailsPlusRow } from "./AvailsPlusCatalogTable";
import { ColumnKey } from "./availsPlusColumnConfig";
import { AvailsWidgetSelection } from "./AvailsSummaryWidgets";

export type FilterableColumnKey = Extract<
    ColumnKey,
    | "content_type"
    | "license_type"
    | "format_profile"
    | "branding_rights"
    | "hdr"
    | "license_rights_description"
>;

export type ColumnFiltersState = Partial<
    Record<FilterableColumnKey, string[]>
>;

export interface FilterOption {
    value: string;
    label: string;
}

export interface ColumnFilterConfig {
    apiParam: string;
    options: FilterOption[];
}

export const FILTERABLE_COLUMN_CONFIG: Record<
    FilterableColumnKey,
    ColumnFilterConfig
> = {
    content_type: {
        apiParam: "contentType",
        options: [
            { value: "movie", label: "Movie" },
            { value: "episode", label: "Episode" },
            { value: "supplemental", label: "Supplemental" },
        ],
    },
    license_type: {
        apiParam: "licenseType",
        options: [
            { value: "FVOD", label: "FVOD" },
            { value: "SVOD", label: "SVOD" },
            { value: "SVOD+FVOD", label: "SVOD+FVOD" },
        ],
    },
    format_profile: {
        apiParam: "formatProfile",
        options: [
            { value: "SD", label: "SD" },
            { value: "HD", label: "HD" },
            { value: "UHD", label: "UHD" },
        ],
    },
    branding_rights: {
        apiParam: "brandingRights",
        options: [
            { value: "True", label: "True" },
            { value: "", label: "(blank)" },
        ],
    },
    hdr: {
        apiParam: "hdr",
        options: [
            { value: "HDR10", label: "HDR10" },
            { value: "", label: "(blank)" },
        ],
    },
    license_rights_description: {
        apiParam: "licenseRightsDescription",
        options: [
            { value: "SWP", label: "SWP" },
            { value: "SDP", label: "SDP" },
            { value: "", label: "(blank)" },
        ],
    },
};

export function isFilterableColumn(
    key: ColumnKey
): key is FilterableColumnKey {
    return key in FILTERABLE_COLUMN_CONFIG;
}

export function encodeFilterValues(values: string[]): string {
    return values
        .map((value) => (value === "" ? "(blank)" : value))
        .join(",");
}

export function appendColumnFiltersToSearchParams(
    params: URLSearchParams,
    columnFilters: ColumnFiltersState
) {
    for (const [columnKey, values] of Object.entries(columnFilters) as [
        FilterableColumnKey,
        string[],
    ][]) {
        if (!values?.length) continue;
        const config = FILTERABLE_COLUMN_CONFIG[columnKey];
        if (!config) continue;
        if (values.length >= config.options.length) continue;
        params.set(config.apiParam, encodeFilterValues(values));
    }
}

export function appendColumnFiltersToRecord(
    target: Record<string, string | string[]>,
    columnFilters: ColumnFiltersState
) {
    const params = new URLSearchParams();
    appendColumnFiltersToSearchParams(params, columnFilters);
    params.forEach((value, key) => {
        target[key] = value;
    });
}

export const FILTERABLE_COLUMN_LABELS: Record<FilterableColumnKey, string> = {
    content_type: "Content Type",
    license_type: "License Type",
    format_profile: "Format Profile",
    branding_rights: "Branding Rights",
    hdr: "HDR10",
    license_rights_description: "License Rights Description",
};

export function hasActiveColumnFilters(
    columnFilters: ColumnFiltersState
): boolean {
    return Object.entries(columnFilters).some(([columnKey, values]) =>
        isColumnFilterActive(columnKey as FilterableColumnKey, columnFilters)
    );
}

export function isColumnFilterActive(
    columnKey: FilterableColumnKey,
    columnFilters: ColumnFiltersState
): boolean {
    const values = columnFilters[columnKey];
    if (!values?.length) return false;
    const config = FILTERABLE_COLUMN_CONFIG[columnKey];
    return config ? values.length < config.options.length : false;
}

export function getColumnFilterSummary(
    columnKey: FilterableColumnKey,
    columnFilters: ColumnFiltersState
): string | null {
    if (!isColumnFilterActive(columnKey, columnFilters)) return null;
    const values = columnFilters[columnKey];
    if (!values?.length) return null;
    const config = FILTERABLE_COLUMN_CONFIG[columnKey];
    return values
        .map(
            (value) =>
                config.options.find((option) => option.value === value)?.label ??
                value
        )
        .join(", ");
}

function normalizeFilterComparableValue(
    columnKey: FilterableColumnKey,
    value: string
): string {
    if (columnKey === "content_type") {
        return value.toLowerCase();
    }
    return value;
}

function compareYmd(a: string, b: string): number {
    if (!a && !b) return 0;
    if (!a) return -1;
    if (!b) return 1;
    return a.localeCompare(b);
}

function utcTodayYmd(): string {
    return new Date().toISOString().slice(0, 10);
}

function addUtcDaysYmd(ymd: string, days: number): string {
    const date = new Date(`${ymd}T12:00:00.000Z`);
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
}

const CATALOG_STATUS_TO_WINDOW: Record<
    "live" | "scheduled" | "expired",
    "current" | "future" | "past"
> = {
    live: "current",
    scheduled: "future",
    expired: "past",
};

export function rowMatchesWidgetSelection(
    row: AvailsPlusRow,
    selection: AvailsWidgetSelection
): boolean {
    if (!selection) return true;

    if (selection.type === "overall") {
        return row.slate_status === selection.slateStatus;
    }

    if (selection.type === "roku") {
        return row.roku_status === selection.rokuStatus;
    }

    if (selection.type === "catalog") {
        const windowStatus = classifyAvailsLicenseWindowStatus(
            row.playback_start_date,
            row.playback_end_date
        );
        return windowStatus === CATALOG_STATUS_TO_WINDOW[selection.status];
    }

    if (selection.type === "syncRequired") {
        if (
            row.slate_status !== "require_sync" &&
            row.slate_status !== "not_exist" &&
            row.roku_status !== "require_sync" &&
            row.roku_status !== "not_exist"
        ) {
            return false;
        }
        const today = utcTodayYmd();
        const startDate = pacificCalendarDateFromAvailsValue(
            row.playback_start_date
        );
        if (!startDate) return false;
        const dateTo = addUtcDaysYmd(today, selection.days);
        return (
            compareYmd(startDate, today) >= 0 &&
            compareYmd(startDate, dateTo) <= 0
        );
    }

    const today = utcTodayYmd();
    const endDate = pacificCalendarDateFromAvailsValue(row.playback_end_date);
    if (!endDate) return false;
    if (selection.bucket === "recentlyExpired") {
        const from = addUtcDaysYmd(today, -14);
        return (
            compareYmd(endDate, from) >= 0 && compareYmd(endDate, today) <= 0
        );
    }
    const to = addUtcDaysYmd(today, 14);
    return compareYmd(endDate, today) >= 0 && compareYmd(endDate, to) <= 0;
}

export function rowMatchesManualDateRange(
    row: AvailsPlusRow,
    from: Dayjs | null,
    to: Dayjs | null
): boolean {
    if (!from?.isValid() && !to?.isValid()) return true;
    const startDate = pacificCalendarDateFromAvailsValue(
        row.playback_start_date
    );
    if (!startDate) return false;
    if (from?.isValid()) {
        const fromYmd = from.format("YYYY-MM-DD");
        if (compareYmd(startDate, fromYmd) < 0) return false;
    }
    const effectiveTo = to?.isValid() ? to : from;
    if (effectiveTo?.isValid()) {
        const toYmd = effectiveTo.format("YYYY-MM-DD");
        if (compareYmd(startDate, toYmd) > 0) return false;
    }
    return true;
}

export interface ActiveViewFilterOptions {
    widgetSelection: AvailsWidgetSelection;
    filterDateFrom: Dayjs | null;
    filterDateTo: Dayjs | null;
    columnFilters: ColumnFiltersState;
}

export function rowMatchesActiveView(
    row: AvailsPlusRow,
    options: ActiveViewFilterOptions
): boolean {
    if (!rowMatchesColumnFilters(row, options.columnFilters)) return false;
    if (options.widgetSelection) {
        return rowMatchesWidgetSelection(row, options.widgetSelection);
    }
    return rowMatchesManualDateRange(
        row,
        options.filterDateFrom,
        options.filterDateTo
    );
}

export function rowMatchesColumnFilters(
    row: AvailsPlusRow,
    columnFilters: ColumnFiltersState
): boolean {
    for (const [columnKey, values] of Object.entries(columnFilters) as [
        FilterableColumnKey,
        string[],
    ][]) {
        if (!values?.length) continue;
        const config = FILTERABLE_COLUMN_CONFIG[columnKey];
        if (!config || values.length >= config.options.length) continue;

        const rowValue = normalizeFilterComparableValue(
            columnKey,
            String(row[columnKey] ?? "")
        );
        const allowedValues = values.map((value) =>
            normalizeFilterComparableValue(columnKey, value)
        );
        if (!allowedValues.includes(rowValue)) {
            return false;
        }
    }
    return true;
}
